import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import dayjs from 'dayjs';
import {equalTo, orderByChild, query, ref} from 'firebase/database';
import {useObject} from 'react-firebase-hooks/database';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {useStoreContext} from '@store/store-context';
import {GoalsRequestImpl} from '@api/goals-api/goals-request';
import {
  AddMentions,
  database,
  FIREBASE_BASE_URL,
} from '@utils/firebase-request';
import {ITaskRequestImpl} from '../get-started/tasks/tasks-requests';
import {goalsFromResponse} from '../goals/my-objective/my-goals-v2.utils';
import {NotificationEvent, NotificationState} from './notification.types';
import {
  actionableGoalIds,
  normalizeActionItemNotifications,
  normalizeCheckinCommentNotifications,
  normalizeGoalActivityNotifications,
  normalizeTaskNotifications,
  sortNotifications,
} from './notification.utils';

const emptyState: NotificationState = {
  readIds: [],
  unreadIds: [],
  dismissedIds: [],
};

const readStoredState = (key: string): NotificationState => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return emptyState;
    const parsed = JSON.parse(stored);
    return {
      readIds: Array.isArray(parsed?.readIds) ? parsed.readIds : [],
      unreadIds: Array.isArray(parsed?.unreadIds) ? parsed.unreadIds : [],
      dismissedIds: Array.isArray(parsed?.dismissedIds)
        ? parsed.dismissedIds
        : [],
    };
  } catch {
    return emptyState;
  }
};

export const useNotificationInbox = () => {
  const {
    authStore: {auth},
    storeAllObjectives,
    storeYouGoals,
    usersStore: {users},
  } = useStoreContext();
  const userId = auth?.user?.id || '';
  const workspaceId = auth?.user?.workspace?.id || '';
  const storageKey = `beam-notification-state:${workspaceId}:${userId}`;
  const [state, setState] = useState<NotificationState>(() =>
    readStoredState(storageKey),
  );
  const {
    isCheckinsEnabled,
    isActionItemEnabled,
    isFeedBackEnabled,
    isGoalEnabled,
    isEngagementActive,
    isPerformanceActive,
  } = useWorkspaceUseCase();

  useEffect(() => {
    setState(readStoredState(storageKey));
  }, [storageKey]);

  useEffect(() => {
    const handleStateChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === storageKey && detail?.state) {
        setState(detail.state);
      }
    };
    window.addEventListener(
      'beam-notification-state-change',
      handleStateChange,
    );
    return () =>
      window.removeEventListener(
        'beam-notification-state-change',
        handleStateChange,
      );
  }, [storageKey]);

  const taskQuery = useQuery(
    ['notification-task-list', workspaceId, userId],
    () => new ITaskRequestImpl().getUserTaskList(userId),
    {
      enabled: Boolean(workspaceId && userId),
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: 1,
    },
  );
  const reminderGoalIds = useMemo(() => {
    const tasks = taskQuery.data?.goals;
    return (Array.isArray(tasks) ? tasks : tasks ? [tasks] : [])
      .map((task: any) => task?.id)
      .filter(Boolean);
  }, [taskQuery.data?.goals]);
  const currentGoalsQuery = useQuery(
    ['notification-goals', workspaceId, userId],
    () =>
      new GoalsRequestImpl().fetchAllGoals(
        {member: userId, paginate: false},
        1,
        1000000,
      ),
    {
      enabled: Boolean(
        workspaceId && userId && isGoalEnabled && reminderGoalIds.length,
      ),
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  );
  const currentGoalIds = useMemo(
    () =>
      currentGoalsQuery.isError
        ? new Set<string>()
        : actionableGoalIds(goalsFromResponse(currentGoalsQuery.data)),
    [currentGoalsQuery.data, currentGoalsQuery.isError],
  );
  const checkinComments = useFirebaseFetch(
    `users/${userId}/mentions/checkins/comments`,
  );
  const goalActivity = useFirebaseFetch(`users/${userId}/mentions/goals`);
  const actionItemsQuery = useMemo(
    () =>
      workspaceId && userId
        ? query(
            ref(database, `${FIREBASE_BASE_URL}${workspaceId}/action_items`),
            orderByChild('assignee'),
            equalTo(userId),
          )
        : null,
    [userId, workspaceId],
  );
  const [actionItemsSnapshot, actionItemsLoading, actionItemsError] = useObject(
    actionItemsQuery,
  );
  const actionItems = useMemo(() => actionItemsSnapshot?.val() || null, [
    actionItemsSnapshot,
  ]);
  const [notificationClock, setNotificationClock] = useState(Date.now());

  useEffect(() => {
    const nextDay = dayjs().add(1, 'day').startOf('day');
    const timeout = window.setTimeout(
      () => setNotificationClock(Date.now()),
      Math.max(nextDay.valueOf() - Date.now() + 1000, 1000),
    );
    return () => window.clearTimeout(timeout);
  }, [notificationClock]);

  const userNames = useMemo(
    () =>
      [auth?.user, ...(users || [])].reduce<Record<string, string>>(
        (names, user) => {
          if (!user?.id) return names;
          names[user.id] =
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email || 'A teammate';
          return names;
        },
        {},
      ),
    [auth?.user, users],
  );

  const notifications = useMemo(() => {
    const taskData = {...(taskQuery.data || {})};
    if (!isEngagementActive || !isCheckinsEnabled) delete taskData.checkin;
    if (!isEngagementActive || !isFeedBackEnabled) {
      delete taskData.feedbackRequests;
    }
    if (!isPerformanceActive) {
      [
        'reviewPeers',
        'managerReviews',
        'peerReviews',
        'ccReviewNominatePeers',
        'ccReviewPeerNominations',
        'ccReviews',
        'selfReviews',
      ].forEach((key) => delete taskData[key]);
    }

    const goalNames = Object.keys(goalActivity.data || {}).reduce<
      Record<string, string>
    >((names, goalId) => {
      const goal =
        storeYouGoals.fetchedGoals.get(goalId) ||
        storeYouGoals.fetchedActiveGoalsById.get(goalId) ||
        storeAllObjectives.fetchedGoals.get(goalId);
      if (goal?.name) names[goalId] = goal.name;
      return names;
    }, {});

    const incoming = [
      ...normalizeTaskNotifications(taskData, userNames, currentGoalIds),
      ...(isCheckinsEnabled
        ? normalizeCheckinCommentNotifications(checkinComments.data)
        : []),
      ...(isGoalEnabled
        ? normalizeGoalActivityNotifications(
            goalActivity.data,
            goalNames,
            userNames,
          )
        : []),
      ...(isActionItemEnabled
        ? normalizeActionItemNotifications(
            actionItems,
            userId,
            userNames,
            notificationClock,
          )
        : []),
    ];
    const readIds = new Set(state.readIds);
    const unreadIds = new Set(state.unreadIds);
    const dismissedIds = new Set(state.dismissedIds);

    return sortNotifications(
      incoming
        .filter((notification) => !dismissedIds.has(notification.id))
        .map((notification) => ({
          ...notification,
          unread:
            unreadIds.has(notification.id) ||
            (notification.unread && !readIds.has(notification.id)),
        })),
    );
  }, [
    checkinComments.data,
    currentGoalIds,
    actionItems,
    goalActivity.data,
    isActionItemEnabled,
    isCheckinsEnabled,
    isEngagementActive,
    isFeedBackEnabled,
    isGoalEnabled,
    isPerformanceActive,
    notificationClock,
    state.dismissedIds,
    state.readIds,
    state.unreadIds,
    taskQuery.data,
    storeAllObjectives.fetchedGoals,
    storeYouGoals.fetchedActiveGoalsById,
    storeYouGoals.fetchedGoals,
    userId,
    userNames,
  ]);

  const persistState = useCallback(
    (nextState: NotificationState) => {
      setState(nextState);
      try {
        localStorage.setItem(storageKey, JSON.stringify(nextState));
      } catch {
        // The inbox remains usable for this session when storage is blocked.
      }
      window.dispatchEvent(
        new CustomEvent('beam-notification-state-change', {
          detail: {key: storageKey, state: nextState},
        }),
      );
    },
    [storageKey],
  );

  const markRead = useCallback(
    (notification: NotificationEvent) => {
      persistState({
        ...state,
        readIds: [...new Set([...state.readIds, notification.id])],
        unreadIds: state.unreadIds.filter((id) => id !== notification.id),
      });
      if (notification.source === 'checkinComments' && notification.sourceId) {
        AddMentions(
          userId,
          notification.sourceId,
          {unreadComment: 0, mentionedBy: []},
          'checkins/comments',
        );
      }
    },
    [persistState, state, userId],
  );

  const markUnread = useCallback(
    (notification: NotificationEvent) => {
      persistState({
        ...state,
        readIds: state.readIds.filter((id) => id !== notification.id),
        unreadIds: [...new Set([...state.unreadIds, notification.id])],
      });
    },
    [persistState, state],
  );

  const dismiss = useCallback(
    (notification: NotificationEvent) => {
      const nextState = {
        ...state,
        readIds: [...new Set([...state.readIds, notification.id])],
        unreadIds: state.unreadIds.filter((id) => id !== notification.id),
        dismissedIds: [...new Set([...state.dismissedIds, notification.id])],
      };
      persistState(nextState);
      if (notification.source === 'checkinComments' && notification.sourceId) {
        AddMentions(
          userId,
          notification.sourceId,
          {unreadComment: 0, mentionedBy: []},
          'checkins/comments',
        );
      }
    },
    [persistState, state, userId],
  );

  const markAllRead = useCallback(() => {
    const unread = notifications.filter((notification) => notification.unread);
    const commentNotifications = unread.filter(
      (notification) =>
        notification.source === 'checkinComments' && notification.sourceId,
    );
    persistState({
      ...state,
      readIds: [...new Set([...state.readIds, ...unread.map(({id}) => id)])],
      unreadIds: state.unreadIds.filter(
        (id) => !unread.some((notification) => notification.id === id),
      ),
    });
    commentNotifications.forEach((notification) => {
      AddMentions(
        userId,
        notification.sourceId as string,
        {unreadComment: 0, mentionedBy: []},
        'checkins/comments',
      );
    });
  }, [notifications, persistState, state, userId]);

  return {
    notifications,
    unreadCount: notifications.filter(({unread}) => unread).length,
    isLoading:
      taskQuery.isLoading ||
      currentGoalsQuery.isLoading ||
      (isCheckinsEnabled && checkinComments.isLoading) ||
      (isGoalEnabled && goalActivity.isLoading) ||
      (isActionItemEnabled && actionItemsLoading),
    isError:
      taskQuery.isError ||
      currentGoalsQuery.isError ||
      (isCheckinsEnabled && Boolean(checkinComments.error)) ||
      (isGoalEnabled && Boolean(goalActivity.error)) ||
      (isActionItemEnabled && Boolean(actionItemsError)),
    refetch: () =>
      Promise.all([
        taskQuery.refetch(),
        ...(reminderGoalIds.length && isGoalEnabled
          ? [currentGoalsQuery.refetch()]
          : []),
      ]),
    markRead,
    markUnread,
    markAllRead,
    dismiss,
  };
};
